import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { DEFAULT_CURRENCY } from "@faire/retailer-visitor-shared/consts/DEFAULT_CURRENCY";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { formatMoney } from "@faire/web--source/common/money";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import React, { useEffect, useRef } from "react";
import styled, { css, keyframes } from "styled-components";
var PRODUCT_IMAGES_URLS = [
    "https://cdn.faire.com/fastly/d05faa9c25f19a7e3eee11c9207d018a80195c55b3f82db78e0fca2cae33850d.png",
    "https://cdn.faire.com/fastly/194f15754f15156f9ed2b1fbe9f27ee7fbebb198f873bd5bbfb5be1f9febc7b0.png",
    "https://cdn.faire.com/fastly/768abc19fd13e2883602c667b1639a113750eebf636f9de371c4245465c64c9f.png"
];
var ANIMATION_INTERVAL_MS = 250;
var PAUSE_DURATION_MS = 2000;
// Color to mimic field selection border
var FIELD_SELECTION_COLOR = "#5DA2E3";
export var ProductDetailsIllustration = function(param) {
    var visible = param.visible;
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var initialPrice = formatMoney({
        money: IFaireMoney.build({
            amount_cents: 1000,
            currency: DEFAULT_CURRENCY
        }),
        omitCentsIfZero: true
    });
    var updatedPrice = formatMoney({
        money: IFaireMoney.build({
            amount_cents: 1200,
            currency: DEFAULT_CURRENCY
        }),
        omitCentsIfZero: true
    });
    var prices = generatePriceAnimationSteps(initialPrice, updatedPrice);
    var _React_useState = _sliced_to_array(React.useState(0), 2), priceIndex = _React_useState[0], setPriceIndex = _React_useState[1];
    var _React_useState1 = _sliced_to_array(React.useState(false), 2), delay = _React_useState1[0], setDelay = _React_useState1[1];
    // Store delay in a ref to avoid using as dependency in useEffect
    var delayRef = React.useRef(delay);
    delayRef.current = delay;
    var delayTimeoutId = useRef(undefined);
    var productName = strictLocalize({
        id: "W67UFt",
        defaultMessage: [
            {
                type: 0,
                value: "Flower pin"
            }
        ]
    });
    useEffect(function() {
        if (!visible) {
            // Reset the cursor animation state when component is hidden
            if (delayRef.current) {
                setDelay(false);
            }
            return;
        }
        var intervalId = setInterval(function() {
            if (!delayRef.current) {
                setPriceIndex(function(prevPriceIndex) {
                    var halfwayPoint = Math.floor(prices.length / 2);
                    var nextPriceIndex = (prevPriceIndex + 1) % prices.length;
                    // Pause animation at halfway point and end to show blinking text cursor
                    if (nextPriceIndex === halfwayPoint || nextPriceIndex === 0) {
                        setDelay(true);
                        clearTimeout(delayTimeoutId.current);
                        delayTimeoutId.current = setTimeout(function() {
                            return setDelay(false);
                        }, PAUSE_DURATION_MS);
                    }
                    return nextPriceIndex;
                });
            }
        }, ANIMATION_INTERVAL_MS);
        return function() {
            clearInterval(intervalId);
            clearTimeout(delayTimeoutId.current);
        };
    }, [
        prices.length,
        visible
    ]);
    if (isTabletAndBelow) {
        return /*#__PURE__*/ _jsxs(Container, {
            justify: "center",
            gap: "3x",
            children: [
                /*#__PURE__*/ _jsx(ProductRow, {
                    imageUrl: PRODUCT_IMAGES_URLS[0]
                }),
                /*#__PURE__*/ _jsx(ProductRow, {
                    imageUrl: PRODUCT_IMAGES_URLS[1],
                    name: productName,
                    price: prices[priceIndex],
                    delay: delay
                }),
                /*#__PURE__*/ _jsx(ProductRow, {
                    imageUrl: PRODUCT_IMAGES_URLS[2]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Container, {
        justify: "center",
        children: [
            /*#__PURE__*/ _jsx(ProductRow, {}),
            /*#__PURE__*/ _jsx(RowDivider, {}),
            /*#__PURE__*/ _jsx(ProductRow, {
                imageUrl: PRODUCT_IMAGES_URLS[0]
            }),
            /*#__PURE__*/ _jsx(RowDivider, {}),
            /*#__PURE__*/ _jsx(ProductRow, {
                imageUrl: PRODUCT_IMAGES_URLS[1],
                name: productName,
                price: prices[priceIndex],
                delay: delay
            }),
            /*#__PURE__*/ _jsx(RowDivider, {}),
            /*#__PURE__*/ _jsx(ProductRow, {
                imageUrl: PRODUCT_IMAGES_URLS[2]
            }),
            /*#__PURE__*/ _jsx(RowDivider, {}),
            /*#__PURE__*/ _jsx(ProductRow, {})
        ]
    });
};
var ProductRow = function(param) {
    var imageUrl = param.imageUrl, name = param.name, price = param.price, delay = param.delay;
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    var isSSR = useIsSSR();
    var url = imageUrl ? optimizeImageUrl({
        url: imageUrl
    }, isSSR) : undefined;
    if (isTabletAndBelow) {
        return /*#__PURE__*/ _jsxs(Row, {
            align: "center",
            gap: "2x",
            children: [
                /*#__PURE__*/ _jsx(ProductImage, {
                    $imageUrl: url
                }),
                /*#__PURE__*/ _jsx(TextContainer, {
                    $width: 112,
                    children: name ? /*#__PURE__*/ _jsx(ProductName, {
                        color: Core.text.subdued,
                        variant: "subheadingSansMedium",
                        children: name
                    }) : /*#__PURE__*/ _jsx(PlaceholderField, {
                        $width: 96
                    })
                }),
                price ? /*#__PURE__*/ _jsxs(PriceField, {
                    align: "center",
                    gap: 5,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            color: Core.text.subdued,
                            variant: "subheadingSansMedium",
                            children: price
                        }),
                        /*#__PURE__*/ _jsx(TextCursor, {
                            $blink: delay
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(PlaceholderField, {
                    $width: 88
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Row, {
        align: "center",
        gap: "3x",
        children: [
            /*#__PURE__*/ _jsx(ProductImage, {
                $imageUrl: url
            }),
            /*#__PURE__*/ _jsx(TextContainer, {
                $width: 144,
                children: name ? /*#__PURE__*/ _jsx(ProductName, {
                    color: Core.text.subdued,
                    variant: "sectionHeaderSansMedium",
                    children: name
                }) : /*#__PURE__*/ _jsx(PlaceholderField, {
                    $width: 130
                })
            }),
            price ? /*#__PURE__*/ _jsxs(PriceField, {
                align: "center",
                gap: 5,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        color: Core.text.subdued,
                        variant: "sectionHeaderSansMedium",
                        children: price
                    }),
                    /*#__PURE__*/ _jsx(TextCursor, {
                        $blink: delay
                    })
                ]
            }) : /*#__PURE__*/ _jsx(PlaceholderField, {
                $width: 88
            })
        ]
    });
};
var generatePriceAnimationSteps = function(initialPrice, updatedPrice) {
    var steps = [];
    // Delete characters from initial price one by one
    for(var i = initialPrice.length; i > 0; i--){
        steps.push(initialPrice.slice(0, i));
    }
    // Type characters from updated price one by one
    for(var i1 = 2; i1 <= updatedPrice.length; i1++){
        steps.push(updatedPrice.slice(0, i1));
    }
    // Delete characters from updated price one by one
    for(var i2 = updatedPrice.length; i2 > 0; i2--){
        steps.push(updatedPrice.slice(0, i2));
    }
    // Type characters from initial price one by one
    for(var i3 = 2; i3 <= initialPrice.length; i3++){
        steps.push(initialPrice.slice(0, i3));
    }
    return steps;
};
var RowDivider = function() {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsx(HorizontalLine, {}),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            })
        ]
    });
};
var Container = styled(Column).withConfig({
    componentId: "sc-dc10de96-0"
})([
    "height:584px;width:100%;max-width:584px;background-color:",
    ";padding:",
    ";user-select:none;overflow:hidden;",
    "{height:343px;padding:",
    ";}"
], Decorative.tint.neutral, getSpacing(0, 0, 0, "4x"), responsiveMediaQueries.tabletAndBelow, getSpacing("3x", "2x"));
var PlaceholderField = styled.div.withConfig({
    componentId: "sc-dc10de96-1"
})([
    "width:",
    "px;height:",
    ";background-color:",
    ";border-radius:1px;"
], function(props) {
    return props.$width;
}, getSpacing("3x"), Core.surface.critical);
var PriceField = styled(Row).withConfig({
    componentId: "sc-dc10de96-2"
})([
    "width:104px;height:",
    ";background-color:",
    ";border:4px solid ",
    ";border-radius:2px;padding:",
    ";",
    "{width:88px;height:",
    ";padding:",
    ";}"
], getSpacing("7x"), Core.surface.inverse, FIELD_SELECTION_COLOR, getSpacing(0, "1x"), responsiveMediaQueries.tabletAndBelow, getSpacing("5x"), getSpacing(0, "1x"));
var blinkAnimation = keyframes([
    "0%,30%,70%,100%{opacity:1;}50%{opacity:0;}"
]);
var TextCursor = styled.div.withConfig({
    componentId: "sc-dc10de96-3"
})([
    "width:2px;height:28px;background-color:",
    ";animation:",
    ";",
    "{height:",
    ";}"
], Core.border.default, function(props) {
    return props.$blink ? css([
        "",
        " ",
        "ms linear 2"
    ], blinkAnimation, PAUSE_DURATION_MS / 2) : "none";
}, responsiveMediaQueries.tabletAndBelow, getSpacing("3x"));
var ProductImage = styled.div.withConfig({
    componentId: "sc-dc10de96-4"
})([
    "width:104px;height:104px;border-radius:2px;background-image:",
    ";background-repeat:no-repeat;background-size:cover;background-color:",
    ";flex-shrink:0;",
    "{width:",
    ";height:",
    ";}"
], function(props) {
    return props.$imageUrl ? css([
        "url(",
        ")"
    ], props.$imageUrl) : "none";
}, Core.surface.critical, responsiveMediaQueries.tabletAndBelow, getSpacing("10x"), getSpacing("10x"));
var TextContainer = styled.div.withConfig({
    componentId: "sc-dc10de96-5"
})([
    "width:",
    "px;"
], function(props) {
    return props.$width;
});
var ProductName = styled(Typography).withConfig({
    componentId: "sc-dc10de96-6"
})([
    "overflow:hidden;text-overflow:ellipsis;"
]);
